import { ChevronsUp, ExternalLink, Grid3x3, Network, UserPlus } from "lucide-react";
import { NX_SCAN } from "../../data/site";
import { useApi, usd } from "../../dashboard/api";
import { EmptyState, ErrorState, Panel, Skeleton } from "./ui";

const TYPE_ICONS = {
    "Matrix Income": Grid3x3,
    "Affiliate Income": Network,
    "Joined Nexora": UserPlus,
};

const TypeIcon = ({ type }) => {
    const Icon = TYPE_ICONS[type] || ChevronsUp;
    return (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/5 bg-nx-soft text-nx-mute">
            <Icon size={13} strokeWidth={1.5} aria-hidden="true" />
        </span>
    );
};

export const ActivityFeed = ({ limit, showSummary = false, summary }) => {
    const { data, loading, error } = useApi("activity");
    const rows = limit && data ? data.slice(0, limit) : data;

    return (
        <Panel title="Platform Activity" className="mt-6" testId="platform-activity" padded={false}>
            {showSummary && summary && (
                <div className="grid grid-cols-2 divide-x divide-white/5 border-b border-white/5">
                    <div className="px-5 py-4" data-testid="activity-members">
                        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-nx-mute">Members Total</p>
                        <p className="mt-1.5 flex items-baseline gap-2">
                            <span className="text-xl font-extrabold tabular text-nx-text">{summary.membersTotal}</span>
                            <span className="text-xs font-bold tabular text-nx-lime">+{summary.membersDelta}</span>
                        </p>
                    </div>
                    <div className="px-5 py-4" data-testid="activity-paid">
                        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-nx-mute">Total USDT Paid</p>
                        <p className="mt-1.5 flex items-baseline gap-2">
                            <span className="text-xl font-extrabold tabular text-nx-text">{usd(summary.totalUsdtPaid)}</span>
                            <span className="text-xs font-bold tabular text-nx-lime">+{usd(summary.paidDelta)}</span>
                        </p>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="space-y-2 p-5">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                </div>
            ) : error ? (
                <ErrorState testId="activity-error" />
            ) : !rows?.length ? (
                <EmptyState title="No activity yet" desc="Platform activity will appear here." testId="activity-empty" />
            ) : (
                <>
                    <table className="hidden w-full text-left md:table" data-testid="activity-table">
                        <thead>
                            <tr className="border-b border-white/5 text-[10px] font-bold uppercase tracking-[0.16em] text-nx-mute">
                                <th className="px-5 py-3 font-bold">Activity</th>
                                <th className="px-4 py-3 font-bold">ID</th>
                                <th className="px-4 py-3 font-bold">Amount</th>
                                <th className="px-4 py-3 font-bold">Time</th>
                                <th className="px-4 py-3 font-bold">Status</th>
                                <th className="px-5 py-3" aria-label="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={i} className="border-b border-white/[0.04] transition-colors duration-150 last:border-0 hover:bg-white/[0.02]" data-testid={`activity-row-${i}`}>
                                    <td className="px-5 py-3">
                                        <span className="flex items-center gap-2.5">
                                            <TypeIcon type={r.type} />
                                            <span className="text-[13px] font-semibold text-nx-text">{r.type}</span>
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-nx-mute">ID {r.accountId}</td>
                                    <td className="px-4 py-3 text-[13px] font-bold tabular">
                                        {r.amount != null ? <span className="text-nx-lime">+{usd(r.amount)} USDT</span> : <span className="text-nx-mute">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-nx-mute">{r.time}</td>
                                    <td className="px-4 py-3">
                                        <span className="rounded-md border border-nx-lime/20 bg-nx-lime/[0.06] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-nx-lime">
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-right">
                                        <a href={NX_SCAN} target="_blank" rel="noopener noreferrer" aria-label="View on BscScan" className="inline-flex text-nx-mute transition-colors duration-150 hover:text-nx-text">
                                            <ExternalLink size={13} strokeWidth={1.5} />
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <ul className="md:hidden" data-testid="activity-list">
                        {rows.map((r, i) => (
                            <li key={i} className="flex items-center gap-3 border-b border-white/[0.04] px-4 py-3.5 last:border-0" data-testid={`activity-item-${i}`}>
                                <TypeIcon type={r.type} />
                                <div className="min-w-0 flex-1">
                                    <p className="text-[13px] font-semibold text-nx-text">{r.type}</p>
                                    <p className="mt-0.5 font-mono text-[11px] text-nx-mute">ID {r.accountId} · {r.time}</p>
                                </div>
                                {r.amount != null && (
                                    <span className="shrink-0 text-[13px] font-bold tabular text-nx-lime">+{usd(r.amount)}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </Panel>
    );
};
